import type { MbcBrandContext } from "@nlc/shared-types";

import type { MBCUIMessage } from "../types/mbc-chat.types";

import { MBC_MASTRA_START_MESSAGE } from "../config/mbc-chat.config";

/** Flatten the text parts of a chat message. */
export function messageText(message: MBCUIMessage): string {
  return message.parts
    .filter((part) => part.type === "text")
    .map((part) => ("text" in part ? part.text : ""))
    .join("")
    .trim();
}

/** The kickoff message is sent to the agent but hidden from the transcript. */
export function isStartMessage(message: MBCUIMessage): boolean {
  return (
    message.role === "user" && messageText(message) === MBC_MASTRA_START_MESSAGE
  );
}

function textMessage(
  id: string,
  role: "assistant" | "user",
  text: string,
): MBCUIMessage {
  return { id, parts: [{ text, type: "text" }], role };
}

/**
 * The brand step happens before the chat agent starts, but the customer's
 * choices should read as the opening of the conversation. This rebuilds those
 * choices as transcript bubbles so they show as chat entries above the agent's
 * first reply.
 */
export function buildGatePreamble(
  brand: MbcBrandContext | null,
): MBCUIMessage[] {
  const preamble: MBCUIMessage[] = [
    textMessage(
      "gate-greeting",
      "assistant",
      "Hi 👋 Let's build your brief together. Do you want to work with an existing brand or create a new one?",
    ),
    textMessage(
      "gate-choice",
      "user",
      brand ? "Use an existing brand" : "Continue without a brand",
    ),
  ];

  if (brand) {
    preamble.push(
      textMessage(
        "gate-brand-question",
        "assistant",
        "Great! Which brand are we creating the request for?",
      ),
      textMessage("gate-brand-answer", "user", brand.name),
    );
  }

  return preamble;
}

/** Opening line shown when a saved draft is reopened in the chat. */
export function resumeRecapMessage(title: null | string): MBCUIMessage {
  const suffix = title ? ` for "${title}"` : "";

  return textMessage(
    "resume-recap",
    "assistant",
    `Welcome back 👋 I've reloaded your brief${suffix} — your progress is on the right. Let's keep going; what would you like to refine?`,
  );
}
